import { getFromLocalStorage, LS_KEYS, setInLocalStorage } from '../window';

interface PendingPayment {
  paymentTime: number,
  orderId: string,
}

/**
 * Check whether a pending payment has expired
 * @param paymentTime
 */
const isPendingPaymentExpired = (paymentTime: number): boolean =>
  paymentTime + (5 * 60) < (new Date()).getTime() / 1000;

/**
 * Get all stored pending payments
 */
const getPendingPayments = (): PendingPayment[] => {
  const storedPayments = getFromLocalStorage(LS_KEYS.PENDING_PAYMENTS);
  return storedPayments ? JSON.parse(storedPayments) as PendingPayment[] : [];
};

/**
 * Get all pending payments, filtering out those that have expired
 */
const getFilteredPendingPayments = (): PendingPayment[] =>
  getPendingPayments().filter(({ paymentTime }) => !isPendingPaymentExpired(paymentTime));

/**
 * Store an updated list of pending payments
 * @param pendingPayments
 */
const storePendingPayments = (pendingPayments: PendingPayment[]): void => {
  setInLocalStorage(LS_KEYS.PENDING_PAYMENTS, JSON.stringify(pendingPayments));
};

/**
 * Check whether there is a non-expired pending payment for a given order
 * @param orderId
 */
const hasPendingPayment = (orderId: string): boolean => getPendingPayments()
  .some(({ orderId: paymentOrderId, paymentTime }) => {
    if (paymentOrderId !== orderId) {
      return false;
    }

    return !isPendingPaymentExpired(paymentTime);
  });

/**
 * Remove the pending payment for a specific order
 * @param orderId
 */
const clearPendingPayment = (orderId: string): void => {
  const pendingPayments = getFilteredPendingPayments()
    .filter(({ orderId: paymentOrderId }) => paymentOrderId !== orderId);

  storePendingPayments(pendingPayments);
};

export {
  clearPendingPayment,
  hasPendingPayment,
};
